import "../../../assets/css/quantity-control.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMinus,
  faCheckCircle,
  faInfoCircle,
  faExclamationCircle,
  faTimesCircle
} from "@fortawesome/free-solid-svg-icons";
import { NonBreakingSpace } from "../NonBreakingSpace";
import { Say } from "../../../languages";
import appSettings from "../../../settings/appSettings";

type QuantityProps = {
  disabled: boolean;
  addQuantity: () => void;
  removeQuantity: () => void;
  quantity: number;
  quantityMin?: number;
  quantityMax?: number;
  visible?: boolean;
  showLabel?: boolean;
  messageType?: "success" | "info" | "warning" | "danger";
};

export function QuantityControl(props: QuantityProps) {
  const {
    disabled,
    addQuantity,
    removeQuantity,
    quantity = 1,
    quantityMin = 1,
    quantityMax = 10,
    visible = true,
    showLabel = true,
    messageType = "info"
  } = props;

  const atMaxQuantity = quantity >= quantityMax;
  const belowMinQuantity = quantity < quantityMin;
  const quantityCount = atMaxQuantity
    ? quantityMax
    : quantity < quantityMin
      ? quantityMin
      : quantity;

  const childrenPhraseKey =
    quantityCount > 1 ? "multiple_enabled.child_plural" : "multiple_enabled.child_singular";

  const alertTypes = {
    success: {
      icon: faCheckCircle,
      className: "alert alert-success"
    },
    info: {
      icon: faInfoCircle,
      className: "alert alert-info"
    },
    warning: {
      icon: faExclamationCircle,
      className: "alert alert-warning"
    },
    danger: {
      icon: faTimesCircle,
      className: "alert alert-danger"
    }
  };

  const alertType = alertTypes[messageType];

  if (!visible) {
    return null;
  }

  return (
    <div className="QuantityControlWrapper quantity-control__wrapper">
      {showLabel && (
        <div className="quantity-control__label eta">
          <Say phrase="multiple_enabled.select_quantity_subheading" renderHtml={true} />
        </div>
      )}
      <div className="QuantityControl quantity-control">
        <button
          className="quantity-button quanity-button__remove"
          data-gtm="true"
          data-btn-region="chosenQty"
          data-testid="deincrementQuantity"
          tabIndex={0}
          disabled={quantityCount <= quantityMin || disabled}
          onClick={removeQuantity}>
          <FontAwesomeIcon icon={faMinus} />
          <span className="sr-only" aria-hidden="true">
            <Say phrase="multiple_enabled.screen_reader_remove" />
          </span>
        </button>
        <div className="quantity-display">
          <span className="quantity-display__count eta">{quantityCount}</span>
          <span className="quantity-display__text milli">
            <Say phrase={childrenPhraseKey} />
          </span>
        </div>
        <button
          className="quantity-button quantity-button__add"
          data-gtm="true"
          data-btn-region="chosenQty"
          data-testid="incrementQuantity"
          tabIndex={1}
          disabled={quantityCount >= quantityMax || disabled}
          onClick={addQuantity}>
          <FontAwesomeIcon icon={faPlus} />
          <span className="sr-only" aria-hidden="true">
            <Say phrase="multiple_enabled.screen_reader_add" />
          </span>
        </button>
      </div>
      {atMaxQuantity ? (
        <div
          className={`quantity-control__message quantity-control__message-max ${alertType.className}`}
          role="alert">
          <FontAwesomeIcon icon={alertType.icon} />
          <NonBreakingSpace />{" "}
          <Say phrase="multiple_enabled.exceeded_max_children_message">
            {appSettings.maxQuantityMessage && <>{appSettings.maxQuantityMessage}</>}
          </Say>
        </div>
      ) : null}
      {belowMinQuantity && !atMaxQuantity ? (
        <div
          className={`quantity-control__message quantity-control__message-min ${alertType.className}`}
          role="alert">
          <FontAwesomeIcon icon={alertType.icon} />
          <NonBreakingSpace /> <Say phrase="errors.general_error_text" />
        </div>
      ) : null}
    </div>
  );
}
